/**
 * App shell — header, role-aware navigation, and the connectivity strip.
 *
 * The navigation differs per role because each role opens the app to do one
 * thing. A retailer's first tab is the catalogue; a driver's is the route.
 * Showing everyone the same five tabs would bury the one they need.
 */

import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useAuth, type RoleCode } from '../lib/auth';
import { useI18n, useT } from '../lib/i18n';
import { useQuery, usePoll } from '../lib/useApi';
import { useToast, AuthImage, Sheet, Button } from './ui';
import { api } from '../lib/api';
import { isDialable } from '../lib/phone';
import * as outbox from '../lib/outbox';
import {
  IconHome, IconCatalogue, IconCart, IconOrders, IconRupee, IconChat,
  IconUsers, IconDashboard, IconCheckCircle, IconBox, IconReport,
  IconSettings, IconBack, IconOffline, IconSync, IconAlert, IconLogout,
  IconTruck, IconCalendar, IconRoute, IconMore, IconPin,
} from './Icons';

// ---------------------------------------------------------------------------
// Connectivity + sync strip
// ---------------------------------------------------------------------------

/**
 * Shown under the header only when something needs saying.
 *
 * Silence is the goal: a strip that is always visible becomes wallpaper and
 * stops being read. It appears when the device is offline, when work is
 * queued, or when a queued item failed.
 */
export function StatusStrip() {
  const t = useT();
  const [online, setOnline] = useState(navigator.onLine);
  const [counts, setCounts] = useState({ pending: 0, failed: 0, syncing: 0 });

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);

    const refresh = () => { void outbox.counts().then(setCounts); };
    refresh();
    const unsub = outbox.subscribe(refresh);

    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      unsub();
    };
  }, []);

  if (counts.failed > 0) {
    return (
      <div className="statusbar statusbar--failed">
        <IconAlert width={14} height={14} />
        {t('app.syncFailed', { n: counts.failed })}
      </div>
    );
  }
  if (!online) {
    return (
      <div className="statusbar statusbar--offline">
        <IconOffline width={14} height={14} />
        {t('app.offline')}
        {counts.pending > 0 && ` · ${t('app.pendingSync', { n: counts.pending })}`}
      </div>
    );
  }
  if (counts.syncing > 0 || counts.pending > 0) {
    return (
      <div className="statusbar statusbar--syncing">
        <IconSync width={14} height={14} />
        {counts.syncing > 0 ? t('app.syncing') : t('app.pendingSync', { n: counts.pending })}
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Account — who you are, and how to stop being signed in
// ---------------------------------------------------------------------------

/**
 * THE ONLY SIGN-OUT IN THE APP USED TO BE UNREACHABLE FOR THREE ROLES.
 *
 * It lived on the "This device" tab of Settings, and `/settings` appears in
 * the navigation of `owner_admin` alone. A driver, a salesperson and a
 * retailer had no control anywhere that led there — reported 3 Oct as "there
 * is no login logout option for driver profile and salesperson".
 *
 * The route itself was never guarded, and the comment on it says plainly that
 * "Settings has a per-device tab that every role needs". The intent was
 * recorded; the control was never added. `IconLogout` was drawn for it and
 * imported by nothing.
 *
 * It matters more than a missing convenience. Sessions last 180 days, and the
 * admin-side device revoke has no caller either — so a driver who hands his
 * phone to someone, or who leaves, could not be signed out by himself OR by
 * the office.
 *
 * IN THE HEADER RATHER THAN THE TAB BAR, because a salesperson already has
 * five tabs and a driver four; a sixth would push the one they came for off
 * the edge of a phone. The header is on every screen of every role, so one
 * control covers all of them with no navigation to redesign.
 */
function AccountButton() {
  const { user, logout } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Field staff carry their own profile: their photo (shown in place of the
  // initials avatar) and any open "documents incomplete" request from the admin
  // (shown as a banner below). Fetched once for field staff; admins don't have
  // a staff profile, so it is skipped for them.
  const isFieldStaff = user?.role === 'salesperson' || user?.role === 'driver' || user?.role === 'field_agent';
  const me = useQuery<{
    documentRequest: { missingDocs: string[]; note: string | null } | null;
    photoKey: string | null;
  }>(isFieldStaff ? '/api/staff/me' : null);
  const request = me.data?.documentRequest ?? null;
  const photoKey = me.data?.photoKey ?? null;

  if (!user) return null;

  const initials = (user.fullName || '?')
    .split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

  return (
    <>
      <button
        type="button"
        className="icon-btn"
        aria-label={t('app.account')}
        onClick={() => setOpen(true)}
        style={photoKey ? { padding: 0, overflow: 'hidden', borderRadius: '50%' } : undefined}
      >
        {/*
          The staff member's own photo when we have it — asked for so a shared
          handset shows a face, not a code. Falls back to initials (a letter
          still answers "whose session is this?" from across a van) while the
          photo loads or when none was uploaded.
        */}
        {photoKey ? (
          <AuthImage
            path={`/api/media/${encodeURIComponent(photoKey)}`}
            alt={user.fullName}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        ) : (
          <span className="t-xs t-semi" aria-hidden>{initials}</span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('app.account')}
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'transparent' }}
        >
          {/* A dropdown anchored under the avatar, not a sheet over the header —
              so it opens below the AK button and leaves the page visible. */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed', top: 54, right: 12, zIndex: 61,
              width: 'min(280px, calc(100vw - 24px))',
              background: 'var(--surface)', border: '1px solid var(--line)',
              borderRadius: 'var(--radius-lg)', boxShadow: '0 12px 32px rgba(9, 12, 20, 0.18)',
              padding: 16, maxHeight: 'calc(100vh - 68px)', overflowY: 'auto',
              animation: 'fade 0.12s ease',
            }}
          >
            <div className="stack-3">
              <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                {photoKey ? (
                  <AuthImage
                    path={`/api/media/${encodeURIComponent(photoKey)}`}
                    alt={user.fullName}
                    style={{ width: 44, height: 44, borderRadius: 12, flex: '0 0 auto', objectFit: 'cover' }}
                  />
                ) : (
                  <span
                    aria-hidden
                    style={{
                      width: 44, height: 44, borderRadius: 12, flex: '0 0 auto',
                      background: 'var(--ink)', color: 'var(--surface)',
                      display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 15,
                    }}
                  >{initials}</span>
                )}
                <div style={{ minWidth: 0 }}>
                  <div className="t-lg t-semi" style={{ lineHeight: 1.2 }}>{user.fullName}</div>
                  <div className="t-sm t-muted">
                    {user.phone}
                    {user.phone && user.role ? ' · ' : ''}
                    {(user.role ?? '').replace(/_/g, ' ')}
                  </div>
                </div>
              </div>

              {/*
                DOCUMENTS NEEDED — the admin has asked this person to re-upload
                something. Shown here, above everything, because it blocks nothing
                but must not be missed; tapping it opens the My-details wizard
                where they re-upload, which resolves the request.
              */}
              {request && (
                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate('/profile'); }}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: 'var(--warn-bg, #fff5e6)', color: 'var(--warn, #c08400)',
                    border: '1px solid var(--warn, #c08400)', borderRadius: 'var(--radius)',
                    padding: 12,
                  }}
                >
                  <div className="t-sm t-semi">Documents needed</div>
                  <div className="t-xs" style={{ marginTop: 2 }}>
                    Please re-upload: {request.missingDocs.join(', ')}.
                    {request.note ? ` ${request.note}` : ''}
                  </div>
                  <div className="t-xs t-semi" style={{ marginTop: 4, textDecoration: 'underline' }}>
                    Update my documents →
                  </div>
                </button>
              )}

              <hr className="divider" />

              <LanguageToggle />

              {/* A salesperson/driver can review and update their own details;
                  changes go to the admin for approval. */}
              {(user.role === 'salesperson' || user.role === 'driver' || user.role === 'field_agent') && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  style={{ width: '100%' }}
                  onClick={() => { setOpen(false); navigate('/profile'); }}
                >
                  {t('app.myDetails')}
                </button>
              )}

              {/*
                Everything else a person might want here — push, storage,
                erasure — already exists on Settings. Duplicating it would
                make two screens that disagree the first time one is edited.
              */}
              <button
                type="button"
                className="btn btn--ghost"
                style={{ width: '100%' }}
                onClick={() => { setOpen(false); navigate('/settings'); }}
              >
                {t('app.settings')}
              </button>

              {!confirming ? (
                <button
                  type="button"
                  className="btn btn--danger"
                  style={{ width: '100%' }}
                  onClick={() => setConfirming(true)}
                >
                  {t('app.logout')}
                </button>
              ) : (
                <div className="stack-2">
                  {/*
                    Said before it happens: a field worker signing out on a
                    patchy connection with unsent deliveries in the outbox
                    would lose them, because the queue is keyed to the
                    session that created it.
                  */}
                  <div className="t-sm">{t('app.logoutConfirm')}</div>
                  <div className="row" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn--ghost grow"
                      onClick={() => setConfirming(false)}
                    >
                      {t('app.cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger grow"
                      onClick={() => { void logout(); }}
                    >
                      {t('app.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function Header({
  title, subtitle, back, actions, titleExtra, titleStyle,
}: {
  title: string;
  subtitle?: ReactNode;
  /** Show a back button. `true` uses history; a string navigates to that path. */
  back?: boolean | string;
  actions?: ReactNode;
  /** Rendered inline next to the title — e.g. a customer's classification badge. */
  titleExtra?: ReactNode;
  /** Style for the title text — e.g. the customer name in its classification colour. */
  titleStyle?: CSSProperties;
}) {
  const navigate = useNavigate();
  const t = useT();

  return (
    <>
      <header className="header">
        {back && (
          <button
            className="icon-btn"
            aria-label={t('app.back')}
            onClick={() => (typeof back === 'string' ? navigate(back) : navigate(-1))}
          >
            <IconBack width={22} height={22} />
          </button>
        )}
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
            {/* `flex: 0 1 auto` overrides `.header h1 { flex: 1 }` so the name only
                takes its own width — otherwise it stretches and shoves titleExtra
                (the classification badge) to the far right. */}
            <h1 className="header__title" style={{ ...titleStyle, flex: '0 1 auto' }}>{title}</h1>
            {titleExtra}
          </div>
          {subtitle && <div className="header__sub t-clip">{subtitle}</div>}
        </div>
        {/* .header__actions: inline on desktop, wraps below the title on a phone. */}
        {actions && <div className="header__actions">{actions}</div>}
        <AccountButton />
      </header>
      <StatusStrip />
    </>
  );
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

interface NavItem {
  to: string;
  labelKey: string;
  Icon: (p: { width?: number; height?: number }) => JSX.Element;
  /** Which badge counter feeds this tab, if any. */
  badge?: 'cart' | 'chat' | 'approvals';
  /** Colour key for the icon + active tint in the admin sidebar (the colour
   *  refresh). One of the c-* palette hues; omitted = neutral. */
  tone?: 'blue' | 'amber' | 'green' | 'purple' | 'teal' | 'pink' | 'red' | 'gray';
}

/**
 * Tabs per role. Four or five each — beyond that they get too narrow to hit
 * reliably with a thumb, which is how mis-taps start.
 */
const NAV: Record<RoleCode, NavItem[]> = {
  customer: [
    { to: '/home', labelKey: 'nav.home', Icon: IconHome, tone: 'blue' },
    { to: '/catalogue', labelKey: 'nav.catalogue', Icon: IconCatalogue, tone: 'purple' },
    { to: '/cart', labelKey: 'nav.cart', Icon: IconCart, badge: 'cart', tone: 'amber' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'green' },
    { to: '/ledger', labelKey: 'nav.ledger', Icon: IconRupee, tone: 'teal' },
  ],
  salesperson: [
    // "Dashboard" is the My Day home; "Stock" opens the live stock position
    // (Total/on-vans/godown/sale price/status by category). The Beat tab was
    // removed — beat is handled from the "Today's beat" card on My Day, where a
    // rep can also request a change — so beat has one home, not two.
    { to: '/day', labelKey: 'nav.dashboard', Icon: IconDashboard, tone: 'blue' },
    { to: '/map', labelKey: 'nav.mapView', Icon: IconRoute, tone: 'purple' },
    { to: '/customers', labelKey: 'nav.customers', Icon: IconUsers, tone: 'pink' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'amber' },
    { to: '/chat', labelKey: 'nav.chat', Icon: IconChat, badge: 'chat', tone: 'teal' },
  ],
  /**
   * MY DAY FIRST, requested 2 Oct.
   *
   * The route tab was first because the route is what a driver spends the day
   * on. But he cannot reach it until the day is open — the vehicle, the
   * location fix and the selfie are all captured there — so the first tab was
   * one that showed him nothing until he had found and used the second. The
   * order now matches the order of the work.
   */
  /**
   * Customers added 7 Oct, reported as "driver cannot add a new customer".
   *
   * He always could. `role_driver` has held `customer.create` since the
   * permission seed — the migration even annotates it "at-the-door customer
   * registration" — and the /customers route has listed `driver` in its
   * guard all along. The screen worked; there was simply no way to get to
   * it, short of typing the URL on a phone.
   *
   * The sixth time on this project that a built, permitted, working feature
   * was indistinguishable from a missing one because nothing linked to it.
   */
  /**
   * Driver's tab bar mirrors the salesperson's, as requested — the driver app
   * runs the same screens (My Day home, live stock, customers, orders, chat).
   */
  driver: [
    { to: '/day', labelKey: 'nav.dashboard', Icon: IconDashboard, tone: 'blue' },
    { to: '/map', labelKey: 'nav.mapView', Icon: IconRoute, tone: 'purple' },
    { to: '/customers', labelKey: 'nav.customers', Icon: IconUsers, tone: 'pink' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'amber' },
    { to: '/chat', labelKey: 'nav.chat', Icon: IconChat, badge: 'chat', tone: 'teal' },
  ],
  // The combined driver-cum-salesperson runs the driver app, so the same tabs.
  field_agent: [
    { to: '/day', labelKey: 'nav.dashboard', Icon: IconDashboard, tone: 'blue' },
    { to: '/map', labelKey: 'nav.mapView', Icon: IconRoute, tone: 'purple' },
    { to: '/customers', labelKey: 'nav.customers', Icon: IconUsers, tone: 'pink' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'amber' },
    { to: '/chat', labelKey: 'nav.chat', Icon: IconChat, badge: 'chat', tone: 'teal' },
  ],
  // Other staff (warehouse / loaders / general). No operational screens — they
  // hold no authority here (migration 0067) — so a deliberately minimal menu:
  // their own details (their home) and a line to the office. Previously they had
  // no nav at all and fell through to the customer menu, which showed them a
  // catalogue and a cart they can do nothing with.
  other_staff: [
    { to: '/profile', labelKey: 'nav.myDetails', Icon: IconUsers, tone: 'blue' },
    { to: '/chat', labelKey: 'nav.chat', Icon: IconChat, badge: 'chat', tone: 'teal' },
  ],
  owner_admin: [
    { to: '/dashboard', labelKey: 'nav.dashboard', Icon: IconDashboard, tone: 'blue' },
    { to: '/approvals', labelKey: 'nav.approvals', Icon: IconCheckCircle, badge: 'approvals', tone: 'red' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'amber' },
    { to: '/stock', labelKey: 'nav.stock', Icon: IconBox, tone: 'purple' },
    { to: '/reports', labelKey: 'nav.reports', Icon: IconReport, tone: 'teal' },
    { to: '/settings', labelKey: 'nav.settings', Icon: IconSettings, tone: 'gray' },
  ],
  manager: [
    { to: '/dashboard', labelKey: 'nav.dashboard', Icon: IconDashboard, tone: 'blue' },
    { to: '/approvals', labelKey: 'nav.approvals', Icon: IconCheckCircle, badge: 'approvals', tone: 'red' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'amber' },
    { to: '/stock', labelKey: 'nav.stock', Icon: IconBox, tone: 'purple' },
    { to: '/reports', labelKey: 'nav.reports', Icon: IconReport, tone: 'teal' },
  ],
  accountant: [
    { to: '/dashboard', labelKey: 'nav.dashboard', Icon: IconDashboard, tone: 'blue' },
    { to: '/orders', labelKey: 'nav.orders', Icon: IconOrders, tone: 'amber' },
    { to: '/ledger', labelKey: 'nav.ledger', Icon: IconRupee, tone: 'green' },
    { to: '/reports', labelKey: 'nav.reports', Icon: IconReport, tone: 'teal' },
  ],
};

/**
 * SECONDARY DESTINATIONS, SIDEBAR ONLY.
 *
 * These used to live in a card at the bottom of the Dashboard, which was the
 * ONLY route to four of them — /products, /manage, /purchases and
 * /business-central appear in no tab bar. So they belong in the navigation.
 *
 * They cannot simply be added to NAV above, because NAV renders BOTH the
 * desktop sidebar and the phone's bottom bar — the switch is CSS at 1024px,
 * not a second component. Appending six items would put twelve tabs across a
 * phone screen, and the comment on NAV is right about why that is unusable.
 *
 * So this group is rendered inside the same <nav> and hidden below 1024px by
 * CSS, while the Dashboard keeps its card on small screens for exactly the
 * same reason. Nothing is unreachable at any width — which matters more here
 * than tidiness, because "the code was fine and no control reached it" has
 * been the defect of this project four times over.
 *
 * owner_admin only. Manager and accountant keep the Dashboard card at every
 * width: their permissions are narrower than this list and I would rather
 * they see one honest card than a sidebar of links that refuse them.
 */
const NAV_SECONDARY: Partial<Record<RoleCode, NavItem[]>> = {
  owner_admin: [
    { to: '/customers', labelKey: 'nav.customers', Icon: IconUsers },
    { to: '/reminders', labelKey: 'nav.reminders', Icon: IconCalendar },
    { to: '/map', labelKey: 'nav.mapView', Icon: IconPin },
    { to: '/products', labelKey: 'nav.products', Icon: IconCatalogue },
    /**
     * SALES INVOICES — ONE ENTRY, BOTH ORIGINS.
     *
     * This was briefly two: the app's own register and a separate "BC sales
     * invoices". The tables are still separate and should be — an app number
     * and a Business Central number cannot share a column the gap report has
     * to reconcile — but that is storage, and it had no business showing up in
     * the navigation. To the person doing the work they are one job. Client,
     * 20 Sep: "both are same and both should be accessible from sales invoice
     * tab".
     *
     * Above Purchases, because selling is the business and buying supports it.
     */
    { to: '/invoices', labelKey: 'nav.salesInvoices', Icon: IconOrders },
    { to: '/purchases', labelKey: 'nav.purchases', Icon: IconTruck },
    { to: '/credit-notes', labelKey: 'nav.creditNotes', Icon: IconRupee },
    { to: '/expenses', labelKey: 'nav.expenses', Icon: IconRupee },
    // The loans module existed on the server with no link anywhere, so the
    // daily EMI alert named a loan the owner could not open.
    { to: '/loans', labelKey: 'nav.loans', Icon: IconRupee },
    { to: '/settlements', labelKey: 'admin.settleCash', Icon: IconRupee },
    // Deposits sit next to settlements deliberately: settlement is cash
    // reaching admin, the deposit is it reaching the bank, and only the second
    // is provable against something the business does not write itself.
    { to: '/bank-deposits', labelKey: 'nav.bankDeposits', Icon: IconRupee },
    { to: '/manage', labelKey: 'nav.manage', Icon: IconUsers },
    /**
     * TARGETS — THE SCREEN ADMIN COULD NOT REACH.
     *
     * `/targets` has been routed to owner_admin and manager since it was
     * built, and the ONLY link to it anywhere was on the salesperson's My Day
     * screen. So the person who sets targets could not open the screen that
     * sets them: an owner had to know the URL and type it.
     *
     * Asked on 3 Oct as "from where admin can set target to salesperson",
     * which is the question a missing link produces. It is the fifth time on
     * this project that working code has sat behind no control — the reason
     * `apiRoutes.test.ts` walks endpoints against screens, and a reason to
     * widen that test to walk ROUTES against links too.
     *
     * Next to Staff & masters, because assigning a target is the same job as
     * assigning a beat: deciding what a person is responsible for.
     */
    { to: '/targets', labelKey: 'nav.targets', Icon: IconReport },
    // Who turned up, where, and the photo. Next to Targets because both are
    // about what a person did today rather than what the business sold.
    { to: '/attendance', labelKey: 'nav.attendance', Icon: IconCalendar },
    // Allocate each field worker's beat and vehicle, carried over until changed.
    // Next to attendance because both are about who is doing what today.
    { to: '/allocations', labelKey: 'nav.allocations', Icon: IconRoute },
    // The same three cuts an admin asks a salesperson for.
    { to: '/requirements', labelKey: 'nav.requirements', Icon: IconReport },
    { to: '/my-summary', labelKey: 'nav.mySummary', Icon: IconReport },
    { to: '/business-central', labelKey: 'nav.businessCentral', Icon: IconSync },
  ],
  // Field staff get a single secondary entry — their follow-ups (item #5). It
  // shows in their sidebar "More" and on the Dashboard quick-links card.
  salesperson: [{ to: '/reminders', labelKey: 'nav.reminders', Icon: IconCalendar }],
  driver: [{ to: '/reminders', labelKey: 'nav.reminders', Icon: IconCalendar }],
  field_agent: [{ to: '/reminders', labelKey: 'nav.reminders', Icon: IconCalendar }],
};

/**
 * Grouping for the sidebar "More" list, by destination path.
 *
 * Derived from NAV_SECONDARY (left untouched) rather than restructuring it, so
 * every existing consumer keeps working and a path that is NOT listed here is
 * never dropped — it falls into a trailing "More" bucket. Same 15 destinations,
 * sorted by the job being done, so the list reads as short labelled sections
 * instead of one long wall. Headers are collapsible (see SecondaryGroup).
 */
type NavIcon = NavItem['Icon'];
type NavTone = NonNullable<NavItem['tone']>;
const SECONDARY_GROUP_ORDER: { labelKey: string; Icon: NavIcon; tone: NavTone; paths: string[] }[] = [
  { labelKey: 'navgroup.sell', Icon: IconCart, tone: 'blue', paths: ['/customers', '/reminders', '/map', '/invoices', '/credit-notes'] },
  { labelKey: 'navgroup.catalogue', Icon: IconCatalogue, tone: 'purple', paths: ['/products', '/purchases'] },
  { labelKey: 'navgroup.money', Icon: IconRupee, tone: 'green', paths: ['/expenses', '/loans', '/settlements', '/bank-deposits'] },
  { labelKey: 'navgroup.team', Icon: IconUsers, tone: 'pink', paths: ['/manage', '/targets', '/attendance', '/allocations'] },
  { labelKey: 'navgroup.insights', Icon: IconReport, tone: 'teal', paths: ['/requirements', '/my-summary', '/business-central'] },
];

/** The role's secondary destinations, arranged into the groups above. */
function groupedSecondary(role: RoleCode): { labelKey: string; Icon: NavIcon; tone: NavTone; items: NavItem[] }[] {
  const items = NAV_SECONDARY[role] ?? [];
  if (items.length === 0) return [];
  const byPath = new Map(items.map((i) => [i.to, i]));
  const used = new Set<string>();
  const groups = SECONDARY_GROUP_ORDER
    .map((g) => ({
      labelKey: g.labelKey,
      Icon: g.Icon,
      tone: g.tone,
      // Sub-items take their group's colour so they read as one set.
      items: g.paths
        .map((p) => byPath.get(p))
        .filter((x): x is NavItem => {
          if (x) used.add(x.to);
          return !!x;
        })
        .map((x) => ({ ...x, tone: g.tone })),
    }))
    .filter((g) => g.items.length > 0);
  // Safety net: a secondary item added later but not placed in a group still
  // shows, under the old "More" label, rather than vanishing from the sidebar —
  // "working code behind no control" has been this project's recurring defect.
  const leftovers = items.filter((i) => !used.has(i.to)).map((i) => ({ ...i, tone: 'gray' as NavTone }));
  if (leftovers.length) groups.push({ labelKey: 'nav.more', Icon: IconMore, tone: 'gray', items: leftovers });
  return groups;
}

/**
 * Whether this role's extra destinations are in the sidebar.
 *
 * The Dashboard asks, so that it hides its quick-links card on desktop ONLY
 * for a role that has somewhere else to go. Hiding it unconditionally is how
 * a screen becomes unreachable.
 */
export function hasSidebarExtras(role: RoleCode | undefined): boolean {
  return role !== undefined && (NAV_SECONDARY[role]?.length ?? 0) > 0;
}

export interface Badges {
  cart: number;
  chat: number;
  approvals: number;
}

/**
 * One collapsible section of the sidebar "More" list.
 *
 * The header is a button that toggles the group, and the open/closed choice is
 * remembered per browser in localStorage (wrapped in try/catch — it can be
 * blocked or empty, and the section must still render). Collapsed is purely a
 * per-viewer convenience, so losing it is harmless. Desktop-only, like the whole
 * "More" block.
 */
function SecondaryGroup({ labelKey, items, renderLink, label, Icon, tone }: {
  labelKey: string;
  items: NavItem[];
  renderLink: (item: NavItem) => ReactNode;
  label: string;
  Icon: NavIcon;
  tone: NavTone;
}) {
  const storeKey = `gd_nav_grp_${labelKey}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(storeKey) === '1'; } catch { return false; }
  });
  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(storeKey, next ? '1' : '0'); } catch { /* storage blocked */ }
      return next;
    });
  };
  return (
    <div className="tabbar__section">
      <button
        type="button"
        className={`tabbar__group tabbar__grouphead nav--${tone}`}
        onClick={toggle}
        aria-expanded={!collapsed}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          width: '100%', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="tabbar__ic" style={{ display: 'grid', placeItems: 'center' }}>
            <Icon width={18} height={18} />
          </span>
          {label}
        </span>
        <span className="tabbar__chev" aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>
          {collapsed ? '▸' : '▾'}
        </span>
      </button>
      {!collapsed && items.map(renderLink)}
    </div>
  );
}

export function TabBar({ badges }: { badges?: Partial<Badges> }) {
  const { user } = useAuth();
  const t = useT();
  if (!user) return null;

  const items = NAV[user.role] ?? NAV.customer;
  const groups = groupedSecondary(user.role);

  const link = ({ to, labelKey, Icon, badge, tone }: NavItem) => {
    const count = badge ? badges?.[badge] ?? 0 : 0;
    return (
      <NavLink
        key={to}
        to={to}
        className={({ isActive }) => [tone ? `nav--${tone}` : '', isActive ? 'active' : ''].filter(Boolean).join(' ')}
      >
        <span className="tabbar__ic" style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
          <Icon width={22} height={22} />
          {count > 0 && (
            <span className="tabbar__badge" aria-label={`${count}`}>
              {count > 99 ? '99+' : count}
            </span>
          )}
        </span>
        <span>{t(labelKey)}</span>
      </NavLink>
    );
  };

  return (
    <nav className="tabbar" aria-label="Main">
      {items.map(link)}
      {/* Hidden below 1024px — see NAV_SECONDARY. On a phone these live in
          the Dashboard's card instead, so the destinations exist either way.
          Grouped into collapsible labelled sections (groupedSecondary). */}
      {groups.length > 0 && (
        <div className="tabbar__more">
          {groups.map((g) => (
            <SecondaryGroup
              key={g.labelKey}
              labelKey={g.labelKey}
              items={g.items}
              renderLink={link}
              label={t(g.labelKey)}
              Icon={g.Icon}
              tone={g.tone}
            />
          ))}
        </div>
      )}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Page wrapper
// ---------------------------------------------------------------------------

/**
 * New-order pop-up for a driver.
 *
 * Polls the driver's assigned-orders list and raises a toast — "New order
 * <number> from <customer>" — the moment an order they did not already have
 * appears, which is how an order routed to their beat (and auto-assigned when
 * they are out) announces itself. Renders nothing; only active for a driver.
 *
 * Seen order ids are kept per driver in localStorage so the toast fires only
 * for genuinely new orders, not for the list they already had on first load
 * and not again after a navigation remounts the page.
 */
/**
 * Say one message aloud, cancelling anything still queued first.
 *
 * The cancel is what stops a burst becoming a continuous alarm: when several
 * orders land at once we speak a single summary, and any half-spoken older
 * utterance is dropped rather than stacked behind it.
 */
function announce(text: string): void {
  try {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
  } catch { /* speech synthesis unavailable */ }
}

function NewOrderWatcher() {
  const { user } = useAuth();
  const toast = useToast();
  const isDriver = user?.role === 'driver' || user?.role === 'field_agent';
  const { data, refetch } = useQuery<{
    items: {
      id: string; order_number: string;
      legal_name?: string | null; trade_name?: string | null; shop_name?: string | null;
    }[];
  }>(isDriver ? '/api/orders/assigned' : null);
  usePoll(refetch, 20000, isDriver);

  // Avoid double-processing the same data object across renders.
  const lastHandled = useRef<unknown>(null);

  useEffect(() => {
    if (!isDriver || !user || !data || data === lastHandled.current) return;
    lastHandled.current = data;

    const key = `gd_seen_orders_${user.id}`;
    let raw: string | null = null;
    try { raw = localStorage.getItem(key); } catch { /* storage blocked */ }
    const firstEver = raw === null;

    let seen = new Set<string>();
    if (!firstEver) {
      try { seen = new Set(JSON.parse(raw as string) as string[]); } catch { seen = new Set(); }
    }

    if (!firstEver) {
      // One announcement per batch, not one per order — several orders arriving
      // together (e.g. when the day opens and the beat's backlog lands) speak a
      // single total, so it never turns into a continuous alarm.
      const newOnes = data.items.filter((o) => !seen.has(o.id));
      const only = newOnes.length === 1 ? newOnes[0] : undefined;
      if (only) {
        const who = only.trade_name || only.legal_name || only.shop_name || 'a customer';
        toast.show(`New order ${only.order_number} from ${who}`, 'ok');
        announce(`A new order received from ${who}.`);
      } else if (newOnes.length > 1) {
        toast.show(`${newOnes.length} new orders received`, 'ok');
        announce(`Total ${newOnes.length} new orders received.`);
      }
    }

    try {
      localStorage.setItem(key, JSON.stringify(data.items.map((o) => o.id)));
    } catch { /* storage blocked — next poll retries */ }
  }, [data, isDriver, user, toast]);

  return null;
}

/**
 * The due-reminder pop-up (item #5).
 *
 * Polls the caller's due follow-ups and raises one at a time as a sheet. A
 * 'call' reminder leads with a one-tap dial (a PWA cannot ring by itself). Done
 * and Snooze act on the server; closing it, or a Call, dismisses it for this
 * session so it does not keep re-popping — it returns on the next sign-in or
 * when the cron's phone notification fires.
 */
interface DueReminder {
  id: string; event_type: string; detail: string | null;
  customer_name: string; customer_trade: string | null; customer_phone: string;
}
function ReminderDueWatcher() {
  const { user } = useAuth();
  const toast = useToast();
  const isStaff = !!user && user.role !== 'customer';
  const { data, refetch } = useQuery<{ items: DueReminder[] }>(isStaff ? '/api/reminders/due' : null, {
    refreshOn: ['reminders'],
  });
  usePoll(refetch, 60000, isStaff);

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const current = (data?.items ?? []).find((r) => !dismissed.has(r.id)) ?? null;
  const dismiss = (id: string) => setDismissed((s) => new Set(s).add(id));

  if (!current) return null;
  const who = current.customer_trade || current.customer_name;
  const label: Record<string, string> = {
    order: 'Follow up on the next order', collection: 'Collect money', call: 'Call', visit: 'Visit', other: 'Follow up',
  };

  async function act(fn: () => Promise<unknown>, done: string) {
    try { await fn(); toast.show(done, 'ok'); void refetch(); }
    catch { toast.show('Could not update.', 'danger'); }
  }

  return (
    <Sheet
      open
      onClose={() => dismiss(current.id)}
      title="Reminder"
      footer={
        <>
          <Button variant="ghost" onClick={() => dismiss(current.id)}>Later</Button>
          <Button variant="ghost" onClick={() => { dismiss(current.id); void act(() => api.post(`/api/reminders/${current.id}/snooze`, { dueDate: new Date(Date.now() + 864e5).toISOString().slice(0, 10) }), 'Snoozed to tomorrow.'); }}>Snooze</Button>
          <Button onClick={() => { dismiss(current.id); void act(() => api.post(`/api/reminders/${current.id}/done`, {}), 'Done.'); }}>Done</Button>
        </>
      }
    >
      <div className="stack-2">
        <div className="t-lg t-semi">{label[current.event_type] ?? 'Follow up'} — {who}</div>
        {current.detail && <div className="t-sm t-muted">{current.detail}</div>}
        {current.event_type === 'call' && isDialable(current.customer_phone) && (
          <a className="btn btn--block" href={`tel:${current.customer_phone}`} onClick={() => dismiss(current.id)}>
            Call now
          </a>
        )}
      </div>
    </Sheet>
  );
}

/**
 * Standard page frame.
 *
 * `wide` switches to the desktop layout used by the admin reports, where the
 * tab bar becomes a sidebar (see the 1024px breakpoint in styles.css).
 */
/**
 * Pending-approval count for the admin roles, polled so it updates live when a
 * field-staff request arrives. Returns 0 for everyone else (no fetch). This is
 * the single source for the red banner, the Approvals nav badge and the phone
 * dot — the indicator the office asked for so a request is never missed.
 */
export function usePendingApprovals(): number {
  const { user } = useAuth();
  const isAdmin = !!user && ['owner_admin', 'manager', 'accountant'].includes(user.role);
  const { data, refetch } = useQuery<{ pending: number }>(
    isAdmin ? '/api/admin/approvals/count' : null,
  );
  usePoll(refetch, 20000, isAdmin);
  return data?.pending ?? 0;
}

/** The red "something is waiting" banner. Click opens the Approvals queue. */
function ApprovalsBanner({ count }: { count: number }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => navigate('/approvals')}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        background: 'var(--danger-bg)', border: '1px solid var(--danger)',
        borderRadius: 'var(--radius)', padding: '12px 14px', cursor: 'pointer',
        marginBottom: 'var(--sp-4)',
      }}
    >
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--danger)', flex: '0 0 auto' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--danger)' }}>
          {t('approvals.waitingBanner', { n: count })}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--danger)', opacity: 0.85 }}>
          {t('approvals.waitingHint')}
        </span>
      </span>
      <span style={{
        background: 'var(--danger)', color: '#fff', borderRadius: 'var(--radius)',
        padding: '8px 16px', fontSize: 13, fontWeight: 600, flex: '0 0 auto',
      }}>
        {t('approvals.review')}
      </span>
    </button>
  );
}

export function Page({
  title, subtitle, back, actions, children, wide, badges, hideNav, titleExtra, titleStyle,
}: {
  title: string;
  subtitle?: ReactNode;
  back?: boolean | string;
  actions?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  badges?: Partial<Badges>;
  hideNav?: boolean;
  /** Inline next to the title — e.g. a customer's classification badge. */
  titleExtra?: ReactNode;
  /** Style for the title text — e.g. the customer name in its classification colour. */
  titleStyle?: CSSProperties;
}) {
  const { user } = useAuth();
  /**
   * The admin desktop layout (persistent left sidebar) is decided by ROLE, not by
   * the `wide` flag. It used to require `wide`, so any admin screen that forgot to
   * pass it — Orders, and several others — silently dropped to the phone bottom
   * bar on desktop, losing the sidebar. `wide` now only controls content width
   * (screen--wide); the sidebar follows the role, so it is consistent everywhere.
   */
  const isAdminLayout = !!user && ['owner_admin', 'manager', 'accountant'].includes(user.role);

  // One polled count drives the Approvals nav badge AND the banner, so every
  // admin screen shows a pending request the moment it arrives.
  const pendingApprovals = usePendingApprovals();
  const mergedBadges = isAdminLayout
    ? { ...badges, approvals: pendingApprovals }
    : badges;
  const onApprovals = useLocation().pathname.startsWith('/approvals');

  return (
    <div className={['app', isAdminLayout ? 'app--admin' : ''].filter(Boolean).join(' ')}>
      <NewOrderWatcher />
      <ReminderDueWatcher />
      {!hideNav && <TabBar badges={mergedBadges} />}
      <div className="grow" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Header title={title} subtitle={subtitle} back={back} actions={actions} titleExtra={titleExtra} titleStyle={titleStyle} />
        {/* Admin screens are full-width by default (the role already defines the
            desktop layout), so every admin page is uniform — no per-screen `wide`
            needed. `wide` still opts a non-admin screen into the wider layout. */}
        <main className={['screen', (wide || isAdminLayout) ? 'screen--wide' : ''].filter(Boolean).join(' ')}>
          {/* Red approvals indicator — shown on every admin screen except the
              Approvals page itself, auto-hiding when nothing is pending. */}
          {isAdminLayout && pendingApprovals > 0 && !onApprovals && (
            <ApprovalsBanner count={pendingApprovals} />
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Language toggle
// ---------------------------------------------------------------------------

export function LanguageToggle() {
  const { locale, setLocale } = useI18n();
  return (
    <div className="segment" style={{ maxWidth: 180 }}>
      <button type="button" aria-pressed={locale === 'en'} onClick={() => setLocale('en')}>
        English
      </button>
      <button type="button" aria-pressed={locale === 'hi'} onClick={() => setLocale('hi')}>
        हिन्दी
      </button>
    </div>
  );
}
